import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import DashboardTab    from "./subscriptions/DashboardTab";
import ListTab         from "./subscriptions/ListTab";
import RemindersTab    from "./subscriptions/RemindersTab";
import BillingTab      from "./subscriptions/BillingTab";
import SupplierSubsTab from "./abonnements/SupplierSubsTab";
import ClientSubsTab   from "./abonnements/ClientSubsTab";
import MetricsTab      from "./abonnements/MetricsTab";
import MailingTab      from "./abonnements/MailingTab";

const SAAS_MODE = process.env.REACT_APP_SAAS_MODE === 'true';
const PLAN_RANK = { solo: 0, tpe: 1, pme: 2, groupe: 3 };
function userPlan() { try { const p = JSON.parse(atob((localStorage.getItem("token")||"..").split(".")[1])); return p.plan||"solo"; } catch { return "solo"; } }

const TABS = [
  { id: "dashboard",    label: "Tableau de bord",      icon: "📊",  section: "saas" },
  { id: "list",         label: "Abonnements",           icon: "📋",  section: "saas" },
  { id: "reminders",    label: "Rappels",               icon: "🔔",  section: "saas" },
  { id: "billing",      label: "Facturation",           icon: "🧾",  section: "saas" },
  { id: "sep",          label: "", icon: "", section: "sep" },
  { id: "supplier",     label: "Charges récurrentes",   icon: "📦",  section: "new", plan: "solo" },
  { id: "client-plans", label: "Plans & Abonnés",       icon: "👥",  section: "new", plan: "pme" },
  { id: "metrics",      label: "MRR / ARR",             icon: "📈",  section: "new", plan: "pme" },
  { id: "mailing",      label: "Mailing Brevo",         icon: "📧",  section: "new", plan: "tpe" },
];

export default function Abonnements({ currentCompanyId, onNavigate }) {
  const [tab, setTab]       = useState("dashboard");
  const [stats, setStats]   = useState(null);
  const [subs, setSubs]     = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.subscriptions.stats(currentCompanyId),
      api.subscriptions.list({ company_id: currentCompanyId }),
    ]).then(([s, list]) => {
      setStats(s);
      setSubs(Array.isArray(list) ? list : []);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!currentCompanyId) {
    return (
      <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔄</div>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Sélectionnez une société pour gérer les abonnements</div>
      </div>
    );
  }

  const activeCount  = subs.filter(s => s.status === "actif").length;
  const expiringSoon = subs.filter(s => {
    if (s.status !== "actif" || !s.end_date) return false;
    const in30 = new Date(); in30.setDate(in30.getDate() + 30);
    return new Date(s.end_date) <= in30;
  }).length;

  const tabProps = { currentCompanyId, stats, subs, loading, onRefresh: loadData };

  return (
    <div>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h2 className="page-title" style={{ marginBottom: 2 }}>Abonnements récurrents</h2>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {activeCount} actif(s) · MRR {stats ? `${(stats.mrr || 0).toLocaleString("fr-FR")} €` : "—"} · ARR {stats ? `${(stats.arr || 0).toLocaleString("fr-FR")} €` : "—"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {expiringSoon > 0 && (
            <button onClick={() => setTab("list")} style={{ background: "#ba751722", border: "1px solid #ba751733", color: "#ba7517", padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 12 }}>
              ⚠️ {expiringSoon} expiration(s) proche(s)
            </button>
          )}
        </div>
      </div>

      {/* Onglets */}
      <div style={{ display: "flex", gap: 2, borderBottom: "2px solid var(--border-color, #e2e8f0)", marginBottom: 20, marginTop: 16, overflowX: "auto", alignItems: "flex-end" }}>
        {TABS.map(t => {
          if (t.section === "sep") return <div key="sep" style={{ width: 1, background: "#e5e7eb", margin: "0 6px", alignSelf: "stretch" }} />;
          const locked = SAAS_MODE && t.plan && PLAN_RANK[userPlan()] < PLAN_RANK[t.plan];
          return (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "9px 16px", fontSize: 13, fontWeight: tab === t.id ? 700 : 500,
              cursor: "pointer", border: "none", background: "transparent",
              color: tab === t.id ? "var(--g)" : "var(--text-muted)",
              borderBottom: `2px solid ${tab === t.id ? "var(--g)" : "transparent"}`,
              marginBottom: -2, whiteSpace: "nowrap", transition: "all 0.12s",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {t.id === "list" && activeCount > 0 && (
                <span style={{ fontSize: 10, background: "#1a9e72", color: "#fff", padding: "1px 5px", borderRadius: 8, fontWeight: 700 }}>{activeCount}</span>
              )}
              {locked && <span style={{ fontSize: 9, background: "#6b7280", color: "#fff", padding: "1px 4px", borderRadius: 5 }}>🔒</span>}
            </button>
          );
        })}
      </div>

      {/* Contenu de l'onglet actif */}
      {tab === "dashboard"    && <DashboardTab {...tabProps} />}
      {tab === "list"         && <ListTab      {...tabProps} />}
      {tab === "reminders"    && <RemindersTab {...tabProps} />}
      {tab === "billing"      && <BillingTab   {...tabProps} />}
      {tab === "supplier"     && <SupplierSubsTab currentCompanyId={currentCompanyId} />}
      {tab === "client-plans" && <ClientSubsTab   currentCompanyId={currentCompanyId} onNavigate={onNavigate} />}
      {tab === "metrics"      && <MetricsTab       currentCompanyId={currentCompanyId} />}
      {tab === "mailing"      && <MailingTab        currentCompanyId={currentCompanyId} />}
    </div>
  );
}
